/**
 * Email & Calendar Settings - Admin Portal
 * Firmflow admin portal: Settings > Email & Calendar
 * 
 * Email: Connect Gmail/Outlook, sync with contacts, view status
 * Calendar: Sync one calendar, connect/disconnect, view sync status
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Mail as MailIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';

function EmailCalendarSettings() {
  const [emailStatus, setEmailStatus] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const fetchEmailStatus = async () => {
    try {
      const response = await api.get('/api/email-integration-status/');
      setEmailStatus(response.data);
    } catch (err) {
      console.error('Error fetching email status:', err);
      setEmailStatus({ gmail_connected: false, outlook_connected: false, total_emails_synced: 0, last_sync: null });
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const response = await enhancedApi.getCalendarStatus();
      setCalendarStatus(response.data);
    } catch (err) {
      console.error('Error fetching calendar status:', err);
      setCalendarStatus({ connected: false });
    }
  };

  useEffect(() => {
    // Handle OAuth redirect query params
    const params = new URLSearchParams(window.location.search);
    const gmailResult = params.get('gmail');
    const calendarResult = params.get('calendar');
    if (gmailResult === 'connected') {
      setSuccess('Gmail connected successfully!');
    } else if (gmailResult === 'error') {
      setError('Failed to connect Gmail. Please try again.');
    }
    if (calendarResult === 'connected') {
      setSuccess('Calendar connected successfully!');
    }
    // Clean up query params without reload
    if (gmailResult || calendarResult) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    const load = async () => {
      setLoading(true);
      await Promise.all([fetchEmailStatus(), fetchCalendarStatus()]);
      setLoading(false);
    };
    load();
  }, []);

  const handleConnectGmail = async () => {
    try {
      setGmailLoading(true);
      setError(null);
      const response = await api.post('/api/gmail-auth/');
      if (response.data.auth_url) {
        // Redirect to Google OAuth — callback redirects back to /settings?gmail=connected
        window.location.href = response.data.auth_url;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to connect Gmail');
    } finally {
      setGmailLoading(false);
    }
  };

  const handleDisconnectGmail = async () => {
    try {
      setGmailLoading(true);
      setError(null);
      await api.delete('/api/gmail-disconnect/');
      setSuccess('Gmail disconnected');
      setEmailStatus(prev => prev ? { ...prev, gmail_connected: false } : { gmail_connected: false, outlook_connected: false });
      await fetchEmailStatus();
    } catch (err) {
      console.error('Error disconnecting Gmail:', err);
      setError('Failed to disconnect Gmail');
    } finally {
      setGmailLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    if (!emailStatus?.gmail_connected) {
      setError('Connect Gmail first to sync calendar. Calendar uses the same Google account as your email.');
      return;
    }
    try {
      setCalendarLoading(true);
      setError(null);
      const response = await enhancedApi.initiateCalendarAuth();
      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        setError('No auth URL received');
      }
    } catch (err) {
      console.error('Error connecting calendar:', err);
      setError(err.response?.data?.error || 'Failed to connect calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    try {
      setCalendarLoading(true);
      setError(null);
      await enhancedApi.disconnectCalendar();
      setSuccess('Calendar disconnected');
      setCalendarStatus(prev => prev ? { ...prev, connected: false } : { connected: false });
      await fetchCalendarStatus();
    } catch (err) {
      console.error('Error disconnecting calendar:', err);
      setError('Failed to disconnect calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const formatLastSync = (lastSync) => {
    if (!lastSync) return 'Never';
    return new Date(lastSync).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 800, py: 3, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
          Email & Calendar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect your email and calendar to sync correspondence with clients and manage scheduling.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Email Section: Email > Accounts > Add account */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #eee' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <MailIcon sx={{ mr: 1.5, color: '#1a38ff' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Email
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect Gmail or Google Workspace to automatically sync email correspondence with your clients.
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1.5 }}>
          Accounts
        </Typography>
        <List dense disablePadding>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {emailStatus?.gmail_connected ? (
                <CheckCircleIcon color="success" />
              ) : (
                <ErrorIcon color="disabled" />
              )}
            </ListItemIcon>
            <ListItemText
              primary="Gmail"
              secondary={emailStatus?.gmail_connected ? 'Connected' : 'Not connected'}
            />
            {emailStatus?.gmail_connected ? (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleDisconnectGmail}
                disabled={gmailLoading}
              >
                {gmailLoading ? <CircularProgress size={20} /> : 'Disconnect'}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                startIcon={gmailLoading ? <CircularProgress size={18} /> : <MailIcon />}
                onClick={handleConnectGmail}
                disabled={gmailLoading}
              >
                {gmailLoading ? 'Connecting...' : 'Add account'}
              </Button>
            )}
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ErrorIcon color="disabled" />
            </ListItemIcon>
            <ListItemText
              primary="Outlook / Microsoft 365"
              secondary="Coming soon"
            />
            <Chip label="Coming soon" size="small" variant="outlined" />
          </ListItem>
        </List>

        {emailStatus?.gmail_connected && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                icon={<SyncIcon />}
                label={`Last sync: ${formatLastSync(emailStatus?.last_sync)}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${(emailStatus?.total_emails_synced || 0).toLocaleString()} emails synced`}
                size="small"
                variant="outlined"
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Calendar Section */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <CalendarIcon sx={{ mr: 1.5, color: '#1a38ff' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Calendar
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sync one Google Calendar to view events and allow clients to book meetings from available slots.
          Calendar syncs with the same Google account as your connected email.
        </Typography>

        {!emailStatus?.gmail_connected && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Connect Gmail first to sync calendar. Calendar uses the same Google account as your email.
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={calendarStatus?.connected ?? false}
              disabled
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              {calendarStatus?.connected ? 'Synced!' : 'Calendar not synced'}
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {calendarStatus?.connected ? (
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              icon={<CheckCircleIcon />}
              label="Synced!"
              color="success"
              size="small"
            />
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={handleDisconnectCalendar}
              disabled={calendarLoading}
            >
              {calendarLoading ? <CircularProgress size={20} /> : 'Disconnect Calendar'}
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            startIcon={calendarLoading ? <CircularProgress size={20} /> : <CalendarIcon />}
            onClick={handleConnectCalendar}
            disabled={calendarLoading || !emailStatus?.gmail_connected}
          >
            {calendarLoading ? 'Connecting...' : 'Sync Calendar'}
          </Button>
        )}

        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
          You can sync one calendar at a time. Google shared or group calendars are supported.
        </Typography>
      </Paper>
    </Box>
  );
}

export default EmailCalendarSettings;
